import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircleOutline,
  RadioButtonUnchecked,
  HourglassEmpty,
  Audiotrack as AudioIcon,
  Movie as MovieIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import PopUp from "../components/PopUp";
import TranscriptionReviewPanel from "../components/transcription/TranscriptionReviewPanel";
import { isVideoFile, isAudioFile } from "../components/MediaPlayer";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import {
  listTranscriptions,
  saveTranscription,
  approveTranscription,
  cancelTranscriptionApproval,
  concludeTranscriptionReview,
  isAbortError,
} from "../utils/transcriptionApi";

/* Largura abaixo da qual o sidebar lateral some e vira menu suspenso (Drawer)
   acionado pelo botão "Arquivos" no header. Mantemos como constante para que
   o valor possa ser ajustado em um único lugar (CSS usa o mesmo breakpoint). */
const SIDEBAR_BREAKPOINT_PX = 1024;

const REVIEW_STATUS_IN_REVIEW = "in_review";
const REVIEW_STATUS_APPROVED = "approved";

/**
 * Página dedicada de revisão de transcrições — carregada na rota
 * `/transcricao/revisar/:jobId`. Equivalente, no fluxo de áudio/vídeo, ao
 * que o `DocumentReviewPage` é para a contestação textual.
 *
 * Responsabilidades:
 *   - Carregar a lista de transcrições do job (GET /transcription_review/{jobId}).
 *   - Manter um seletor com todos os arquivos transcritos do job (sidebar
 *     esquerdo); pré-selecionar o primeiro.
 *   - Renderizar o `TranscriptionReviewPanel` para o arquivo selecionado.
 *   - Coordenar Save/Approve/CancelApproval/Conclude com o backend.
 *   - Antes de mudar de arquivo com alterações pendentes, perguntar ao
 *     usuário se ele realmente quer descartar suas alterações.
 *   - Habilitar "Concluir revisão" só quando todas as transcrições estiverem
 *     `approved` (o backend ainda valida via 409 como fallback).
 */
function TranscriptionReviewPage() {
  const history = useHistory();
  const { jobId } = useParams();
  const { token } = useAuth();
  const { t, formatDateTime } = useTranslation("common");

  const [items, setItems] = useState([]); // TranscriptionItem[] tal como vem do backend
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  /* Por id, o estado local da edição: conteúdo "salvo" + flag dirty.
     Usado para habilitar/desabilitar concluir e impedir trocas inadvertidas. */
  const dirtyMapRef = useRef(new Map());
  const [, forceTick] = useState(0); // mecanismo barato para refletir o dirtyMap

  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [cancelingApproval, setCancelingApproval] = useState(false);
  const [concluding, setConcluding] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [pendingTargetId, setPendingTargetId] = useState(null); // id que aguarda confirmação para troca
  /* Lista de arquivos retrátil: o usuário pode dar mais espaço à área de
     transcrição; o estado é mantido apenas em memória (perde-se ao sair
     da rota), o que é ok dado o uso pontual. */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  /* Em telas estreitas, o sidebar vira um menu suspenso (Drawer) acionado
     pelo botão "Arquivos" no header. Mantemos um observer simples de
     largura — `useMediaQuery` do MUI v4 funcionaria, mas só observamos uma
     única condição, então um effect dedicado é mais leve e explícito. */
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < SIDEBAR_BREAKPOINT_PX
      : false,
  );
  const [filesDrawerOpen, setFilesDrawerOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = () => {
      setIsNarrow(window.innerWidth < SIDEBAR_BREAKPOINT_PX);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  /* Quando o usuário escolhe um arquivo dentro do drawer (modo narrow),
     fechamos automaticamente para devolver foco/espaço à transcrição. */
  useEffect(() => {
    if (!isNarrow) setFilesDrawerOpen(false);
  }, [isNarrow]);

  /* ---------------- carregamento ---------------- */

  /* Mantemos `t` em ref para que mensagens de fallback usem a tradução
     atual sem que o efeito de carregamento dependa de `t` (que muda quando
     o idioma muda) — evita disparar nova request a cada render. */
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  /* "Circuit breaker": após uma falha (404, 403, etc.), não tentamos
     recarregar automaticamente o mesmo (jobId, token). O usuário pode
     forçar um retry pelo botão de erro. Sem isso, qualquer dependência
     instável poderia ressuscitar o loop apertado. */
  const failedKeyRef = useRef(null);

  const buildLoadKey = useCallback(
    (j, tk) => (j && tk ? `${j}::${tk}` : null),
    [],
  );

  const loadItems = useCallback(
    async ({ signal, force = false } = {}) => {
      if (!jobId || !token) return;
      const key = buildLoadKey(jobId, token);
      if (!force && failedKeyRef.current === key) {
        /* Última tentativa para esta chave falhou; não martelamos a API. */
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const data = await listTranscriptions(token, jobId, { signal });
        if (signal?.aborted) return;
        failedKeyRef.current = null;
        const list = Array.isArray(data?.transcriptions) ? data.transcriptions : [];
        setItems(list);
        if (list.length > 0) {
          setSelectedId((prev) =>
            prev && list.some((it) => it.id === prev) ? prev : list[0].id,
          );
        } else {
          setSelectedId(null);
        }
      } catch (err) {
        if (isAbortError(err) || signal?.aborted) return;
        failedKeyRef.current = key;
        setLoadError(
          err?.message || tRef.current("transcription.page.loadError"),
        );
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [jobId, token, buildLoadKey],
  );

  useEffect(() => {
    const controller = new AbortController();
    /* `force: true` em montagens/troca de chave: a guarda do circuit breaker
       só vale para re-execuções do mesmo (jobId, token). */
    failedKeyRef.current = null;
    loadItems({ signal: controller.signal, force: true });
    return () => controller.abort();
  }, [loadItems]);

  const handleRetryLoad = useCallback(() => {
    failedKeyRef.current = null;
    const controller = new AbortController();
    loadItems({ signal: controller.signal, force: true });
  }, [loadItems]);

  const selectedItem = useMemo(
    () => items.find((it) => it.id === selectedId) || null,
    [items, selectedId],
  );

  const allApproved =
    items.length > 0 &&
    items.every((it) => it.review_status === REVIEW_STATUS_APPROVED);

  /* ---------------- helpers de mutação ---------------- */

  const updateItemLocal = useCallback((id, partial) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...partial } : it)),
    );
  }, []);

  const markDirty = useCallback((id, isDirty) => {
    const map = dirtyMapRef.current;
    if (isDirty) map.set(id, true);
    else map.delete(id);
    forceTick((n) => n + 1);
  }, []);

  /* Controller de lifetime da página: aborta qualquer mutação em curso
     quando o componente é desmontado (ex.: usuário sai da rota). Sem
     isso, requests órfãs continuam até o backend responder e podem
     disparar setState pós-unmount. */
  const lifetimeControllerRef = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    lifetimeControllerRef.current = controller;
    return () => {
      controller.abort();
      lifetimeControllerRef.current = null;
    };
  }, []);

  const getLifetimeSignal = useCallback(
    () => lifetimeControllerRef.current?.signal,
    [],
  );

  /* ---------------- ações ---------------- */

  const handleSave = useCallback(
    async (content) => {
      if (!selectedItem) return;
      setSaving(true);
      setActionError(null);
      try {
        await saveTranscription(token, jobId, selectedItem.id, content, {
          signal: getLifetimeSignal(),
        });
        updateItemLocal(selectedItem.id, {
          transcription: content,
          review_status: REVIEW_STATUS_IN_REVIEW,
          last_saved_timestamp: new Date().toISOString(),
          approved_by: null,
          approval_timestamp: null,
        });
        markDirty(selectedItem.id, false);
      } catch (err) {
        if (isAbortError(err)) return;
        setActionError(
          err?.message || tRef.current("transcription.page.saveError"),
        );
      } finally {
        if (!getLifetimeSignal()?.aborted) setSaving(false);
      }
    },
    [selectedItem, token, jobId, updateItemLocal, markDirty, getLifetimeSignal],
  );

  const handleApprove = useCallback(
    async (content) => {
      if (!selectedItem) return;
      setApproving(true);
      setActionError(null);
      try {
        const data = await approveTranscription(
          token,
          jobId,
          selectedItem.id,
          content,
          { signal: getLifetimeSignal() },
        );
        updateItemLocal(selectedItem.id, {
          transcription: content,
          review_status: REVIEW_STATUS_APPROVED,
          last_saved_timestamp:
            data?.last_saved_timestamp || new Date().toISOString(),
          approved_by: data?.approved_by ?? null,
          /* `approved_by_username` é o nome legível do aprovador. O backend
             ainda não devolve este campo (planejado — ver doc de mudanças
             do backend); enquanto isso ficamos com `null` aqui e a status
             bar resolve por fallback (nome do usuário logado quando ele
             mesmo aprovou). Ao chegar do backend, o painel passa a exibir
             para qualquer aprovador. */
          approved_by_username: data?.approved_by_username ?? null,
          approval_timestamp:
            data?.approval_timestamp || new Date().toISOString(),
        });
        markDirty(selectedItem.id, false);
      } catch (err) {
        if (isAbortError(err)) return;
        setActionError(
          err?.message || tRef.current("transcription.page.approveError"),
        );
      } finally {
        if (!getLifetimeSignal()?.aborted) setApproving(false);
      }
    },
    [selectedItem, token, jobId, updateItemLocal, markDirty, getLifetimeSignal],
  );

  const handleCancelApproval = useCallback(
    /* `cancelTranscriptionApproval` aponta para o futuro endpoint de reopen
       (`POST /transcription_review/{job_id}/{transcription_id}/reopen`).
       Enquanto o backend não publica o endpoint, o cliente do API pode
       continuar usando `/save` como fallback transparente. Aqui não
       precisamos mais enviar `content`: a operação é puramente de
       desbloqueio (não persiste edições). */
    async () => {
      if (!selectedItem) return;
      setCancelingApproval(true);
      setActionError(null);
      try {
        await cancelTranscriptionApproval(token, jobId, selectedItem.id, {
          signal: getLifetimeSignal(),
        });
        updateItemLocal(selectedItem.id, {
          review_status: REVIEW_STATUS_IN_REVIEW,
          approved_by: null,
          approved_by_username: null,
          approval_timestamp: null,
        });
      } catch (err) {
        if (isAbortError(err)) return;
        setActionError(
          err?.message ||
            tRef.current("transcription.page.cancelApprovalError"),
        );
      } finally {
        if (!getLifetimeSignal()?.aborted) setCancelingApproval(false);
      }
    },
    [selectedItem, token, jobId, updateItemLocal, getLifetimeSignal],
  );

  const handleConclude = useCallback(async () => {
    setConcluding(true);
    setActionError(null);
    try {
      await concludeTranscriptionReview(token, jobId, {
        signal: getLifetimeSignal(),
      });
      /* Após concluir, voltamos para a listagem (a próxima task assume). */
      history.push("/contestacoes");
    } catch (err) {
      if (isAbortError(err)) return;
      setActionError(
        err?.message || tRef.current("transcription.page.concludeError"),
      );
    } finally {
      if (!getLifetimeSignal()?.aborted) setConcluding(false);
    }
  }, [token, jobId, history, getLifetimeSignal]);

  /* ---------------- troca de arquivo (com guard de unsaved) ---------------- */

  const requestSelect = useCallback(
    (id) => {
      if (id === selectedId) return;
      const isDirty =
        selectedId != null && dirtyMapRef.current.get(selectedId) === true;
      if (isDirty) {
        setPendingTargetId(id);
        return;
      }
      setSelectedId(id);
    },
    [selectedId],
  );

  const confirmDiscardAndSwitch = useCallback(() => {
    if (pendingTargetId == null) return;
    if (selectedId != null) markDirty(selectedId, false);
    setSelectedId(pendingTargetId);
    setPendingTargetId(null);
  }, [pendingTargetId, selectedId, markDirty]);

  const cancelSwitch = useCallback(() => setPendingTargetId(null), []);

  const handleBack = useCallback(() => {
    history.goBack ? history.goBack() : history.push("/contestacoes");
  }, [history]);

  /* ---------------- callback de "dirty" do painel ---------------- */

  /* O painel é a fonte da verdade do estado dirty (ele compara seu draft
     com a versão salva). Recebemos o evento aqui para alimentar o
     dirtyMap usado pela guarda de troca de arquivo. */
  const handleDirtyChange = useCallback(
    (id, dirty) => {
      markDirty(id, dirty);
    },
    [markDirty],
  );

  /* ---------------- props estáveis para o painel ----------------

     Estabilizar referências aqui é CRÍTICO: o painel tem efeitos que
     dependem de `onDirtyChange`/`mediaFile`/`metadata`. Se forem objetos
     ou lambdas inline, eles mudariam toda renderização do pai e
     dispararíam ciclos render → forceTick → render → ... que travavam a
     aba do navegador (especialmente perceptível ao tocar mídia ou abrir
     diálogos pesados como o de edição de speaker). */

  const selectedItemId = selectedItem?.id ?? null;
  const selectedItemToken = selectedItem?.token ?? null;
  const selectedItemFilename = selectedItem?.filename ?? null;
  /* Identificação do aprovador. O backend devolve o ID (`approved_by`,
     integer) e — após a mudança documentada no backend — passará a
     devolver também `approved_by_username` (nome legível). Repassamos
     ambos para a status bar resolver a exibição. */
  const selectedItemApprovedById = selectedItem?.approved_by ?? null;
  const selectedItemApprovedByUsername =
    selectedItem?.approved_by_username ?? null;
  const selectedItemApprovalTimestamp = selectedItem?.approval_timestamp ?? null;
  const selectedItemLastSavedTimestamp =
    selectedItem?.last_saved_timestamp ?? null;

  /* O `MediaPlayer` precisa do `token` para baixar o arquivo via
     `/files/download?token=...`. O backend devolve o `token` em cada item
     de `transcriptions[]` (ver `transcriptions.md`). Só montamos o objeto
     `mediaFile` quando o `token` está presente — caso contrário, o player
     teria que ficar tentando baixar uma URL com `token=null`, o que
     resulta em 401/404 silenciosos. Quando `token` está ausente, deixamos
     `mediaFile` undefined e o player exibe apenas o estado vazio. */
  const panelMediaFile = useMemo(() => {
    if (!selectedItemToken) return undefined;
    return { token: selectedItemToken, filename: selectedItemFilename };
  }, [selectedItemToken, selectedItemFilename]);

  const panelMetadata = useMemo(
    () => ({
      approved_by: selectedItemApprovedById,
      approved_by_username: selectedItemApprovedByUsername,
      approval_timestamp: selectedItemApprovalTimestamp,
      last_saved_timestamp: selectedItemLastSavedTimestamp,
    }),
    [
      selectedItemApprovedById,
      selectedItemApprovedByUsername,
      selectedItemApprovalTimestamp,
      selectedItemLastSavedTimestamp,
    ],
  );

  const panelOnDirtyChange = useCallback(
    (dirty) => {
      if (selectedItemId == null) return;
      handleDirtyChange(selectedItemId, dirty);
    },
    [handleDirtyChange, selectedItemId],
  );

  /* ---------------- render ---------------- */

  const renderStatusIcon = (status) => {
    switch (status) {
      case REVIEW_STATUS_APPROVED:
        return (
          <CheckCircleOutline className="transcription-page__file-icon transcription-page__file-icon--approved" />
        );
      case REVIEW_STATUS_IN_REVIEW:
        return (
          <HourglassEmpty className="transcription-page__file-icon transcription-page__file-icon--in-review" />
        );
      default:
        return (
          <RadioButtonUnchecked className="transcription-page__file-icon transcription-page__file-icon--pending" />
        );
    }
  };

  const renderFileMediaIcon = (filename) => {
    if (isVideoFile(filename)) return <MovieIcon fontSize="small" />;
    if (isAudioFile(filename)) return <AudioIcon fontSize="small" />;
    return <AudioIcon fontSize="small" />;
  };

  /* Bloco rolável da lista de arquivos (compartilhado entre o sidebar
     lateral em telas largas e o Drawer em telas estreitas). Mantém-se
     como uma simples função para evitar a sobrecarga de extrair em um
     novo componente — toda a comunicação acontece via o estado da página. */
  const renderFileListContent = ({ inDrawer = false } = {}) => (
    <Box className="transcription-page__file-list">
      {loading && (
        /* Estado de carregamento: como aqui só há UMA chamada
           (GET listTranscriptions), em vez de etapas com checkmarks
           usamos uma barra de progresso INDETERMINADA — uma faixa
           laranja que percorre continuamente o trilho até o conteúdo
           chegar. Comunica "estamos trabalhando" sem prometer um
           tempo específico, evitando o efeito "tela congelada". */
        <Box className="transcription-page__loading-wrap">
          <Typography
            variant="body2"
            className="transcription-page__loading"
          >
            {t("transcription.page.loading")}
          </Typography>
          <div
            className="transcription-page__loading-bar"
            role="progressbar"
            aria-busy="true"
            aria-label={t("transcription.page.loading")}
          >
            <div className="transcription-page__loading-bar-fill" />
          </div>
        </Box>
      )}
      {!loading && loadError && (
        <Box className="transcription-page__load-error">
          <Typography variant="body2" color="error">
            {loadError}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={handleRetryLoad}
            className="transcription-page__retry-btn"
          >
            {t("transcription.page.retry")}
          </Button>
        </Box>
      )}
      {!loading && !loadError && items.length === 0 && (
        <Typography variant="body2">
          {t("transcription.page.empty")}
        </Typography>
      )}
      {!loading &&
        !loadError &&
        items.map((it) => {
          const isSelected = it.id === selectedId;
          const isDirty = dirtyMapRef.current.get(it.id) === true;
          return (
            <Box
              key={it.id}
              className={[
                "transcription-page__file-item",
                isSelected ? "is-selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => {
                requestSelect(it.id);
                if (inDrawer) setFilesDrawerOpen(false);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  requestSelect(it.id);
                  if (inDrawer) setFilesDrawerOpen(false);
                }
              }}
            >
              {renderStatusIcon(it.review_status)}
              <Box className="transcription-page__file-meta">
                <Box className="transcription-page__file-name-row">
                  {renderFileMediaIcon(it.filename)}
                  <Typography
                    variant="body2"
                    className="transcription-page__file-name"
                    title={it.filename}
                  >
                    {it.filename}
                  </Typography>
                  {isDirty && (
                    <span
                      className="transcription-page__dirty-dot"
                      aria-label={t("transcription.page.unsaved")}
                      title={t("transcription.page.unsaved")}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  className="transcription-page__file-status"
                >
                  {t(`transcription.page.statusLabel.${it.review_status}`)}
                  {it.approval_timestamp
                    ? ` • ${formatDateTime(it.approval_timestamp)}`
                    : it.last_saved_timestamp
                      ? ` • ${formatDateTime(it.last_saved_timestamp)}`
                      : ""}
                </Typography>
              </Box>
            </Box>
          );
        })}
    </Box>
  );

  return (
    <Box className="transcription-page-root">
      <Box className="transcription-page-header">
        <Tooltip title={t("transcription.page.back")}>
          <IconButton
            size="small"
            className="transcription-page__back-btn"
            onClick={handleBack}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* Botão "Arquivos" só aparece em telas estreitas (substitui o
            sidebar lateral por um menu suspenso). Em telas largas o sidebar
            inline já está sempre visível e não precisamos do botão. */}
        {isNarrow && (
          <Tooltip title={t("transcription.page.openFiles")}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<MenuIcon fontSize="small" />}
              className="transcription-page__files-menu-btn"
              onClick={() => setFilesDrawerOpen(true)}
            >
              {t("transcription.page.filesMenu")}
              {items.length > 0 ? ` (${items.length})` : ""}
            </Button>
          </Tooltip>
        )}
        <Typography variant="h6" className="transcription-page__title">
          {t("transcription.page.title")}
        </Typography>
        <Box className="transcription-page__header-spacer" />
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!allApproved || concluding || items.length === 0}
          onClick={handleConclude}
        >
          {concluding
            ? t("transcription.page.concluding")
            : t("transcription.page.concludeReview")}
        </Button>
      </Box>

      <Box className="transcription-page-body">
        {/* Sidebar lateral (modo "wide"): só renderiza quando NÃO estamos
            em modo narrow. Em narrow, a lista vira Drawer (abaixo). */}
        {!isNarrow && (
          <Box
            className={[
              "transcription-page__sidebar",
              sidebarCollapsed ? "is-collapsed" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <Box className="transcription-page__sidebar-header">
              {!sidebarCollapsed && (
                <Typography
                  variant="subtitle2"
                  className="transcription-page__sidebar-title"
                >
                  {t("transcription.page.filesTitle", { count: items.length })}
                </Typography>
              )}
              <Tooltip
                title={
                  sidebarCollapsed
                    ? t("transcription.page.expandSidebar")
                    : t("transcription.page.collapseSidebar")
                }
                placement="right"
              >
                <IconButton
                  size="small"
                  onClick={() => setSidebarCollapsed((v) => !v)}
                  className="transcription-page__sidebar-toggle"
                  aria-label={
                    sidebarCollapsed
                      ? t("transcription.page.expandSidebar")
                      : t("transcription.page.collapseSidebar")
                  }
                >
                  {sidebarCollapsed ? (
                    <ChevronRightIcon fontSize="small" />
                  ) : (
                    <ChevronLeftIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
            {!sidebarCollapsed && renderFileListContent()}
          </Box>
        )}

        <Box className="transcription-page__main">
          {selectedItem ? (
            <TranscriptionReviewPanel
              key={selectedItem.id}
              mode="REVISAO"
              transcription={selectedItem.transcription}
              mediaFile={panelMediaFile}
              metadata={panelMetadata}
              saving={saving}
              approving={approving}
              cancelingApproval={cancelingApproval}
              onSave={handleSave}
              onApprove={handleApprove}
              onCancelApproval={handleCancelApproval}
              onDirtyChange={panelOnDirtyChange}
              error={actionError}
              jobId={jobId}
              transcriptionId={selectedItem.id}
            />
          ) : (
            <Box className="transcription-page__placeholder">
              <Typography variant="body1">
                {t("transcription.page.selectAFile")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Drawer (menu suspenso) com a lista de arquivos — usado apenas
          em telas estreitas. O conteúdo é o mesmo do sidebar lateral. */}
      <Drawer
        anchor="left"
        open={isNarrow && filesDrawerOpen}
        onClose={() => setFilesDrawerOpen(false)}
        PaperProps={{ className: "transcription-page__drawer-paper" }}
      >
        <Box className="transcription-page__drawer-header">
          <Typography
            variant="subtitle2"
            className="transcription-page__sidebar-title"
          >
            {t("transcription.page.filesTitle", { count: items.length })}
          </Typography>
          <Tooltip title={t("transcription.page.closeFiles")}>
            <IconButton
              size="small"
              onClick={() => setFilesDrawerOpen(false)}
              className="transcription-page__sidebar-toggle"
              aria-label={t("transcription.page.closeFiles")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {renderFileListContent({ inDrawer: true })}
      </Drawer>

      <PopUp
        open={pendingTargetId != null}
        onClose={cancelSwitch}
        title={t("transcription.page.unsavedDialogTitle")}
      >
        <Typography variant="body2" className="transcription-page__dialog-text">
          {t("transcription.page.unsavedDialogText")}
        </Typography>
        <Box className="transcription-page__dialog-actions">
          <Button onClick={cancelSwitch} variant="outlined">
            {t("cancel")}
          </Button>
          <Button
            onClick={confirmDiscardAndSwitch}
            color="primary"
            variant="contained"
          >
            {t("transcription.page.discardAndSwitch")}
          </Button>
        </Box>
      </PopUp>
    </Box>
  );
}

export default TranscriptionReviewPage;
